import { useMemo } from "react";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import useActiveFilterGenerator from "app/pages/sales-intelligence/sub-modules/signals/hooks/useActiveFilterGenerator";
import SignalPeriodsFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalPeriodsFilter/SignalPeriodsFilter";
import SignalTrafficDirectionsFilter from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalTrafficDirectionsFilter/SignalTrafficDirectionsFilter";
import SignalFiltersWrapper from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalFiltersWrapper/SignalFiltersWrapper";
import { StyledDropdownLabel } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsDropdownLabel/styles";
import type { EcommerceChangesPeriod } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceChangesPeriod";
import type { EcommerceChangesTrend } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceChangesTrend";
import type EcommerceSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";
import type EcommerceSignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/ecommerce/EcommerceSignalFiltersViewProps";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

type Props = EcommerceSignalFiltersViewProps & {
    directionOptions: readonly EcommerceChangesTrend[];
    periodOptions: readonly EcommerceChangesPeriod[];
};

const EcommerceChangeFiltersView = (props: Props) => {
    const { filters, onFiltersChange, directionOptions, periodOptions } = props;

    const change = filters.change;
    const metric = change?.metric;

    const changeAutoOpenSlice = useMemo(
        () =>
            ({
                direction: change?.direction,
                period: change?.period,
            }) as Record<string, unknown>,
        [change?.direction, change?.period],
    );

    const { currentActiveKey, setNextActiveKey } = useActiveFilterGenerator(changeAutoOpenSlice);

    const emitChangeUpdate = (patch: Partial<NonNullable<EcommerceSignalFilters["change"]>>): void => {
        const key = currentActiveKey;
        const shouldAdvance =
            key !== null &&
            key !== undefined &&
            (change === undefined || change[key as "direction" | "period"] === undefined);

        onFiltersChange({
            change: {
                ...change,
                ...patch,
            },
        });

        if (shouldAdvance) {
            setNextActiveKey();
        }
    };

    const handleDirectionChange = (direction: EcommerceChangesTrend): void => {
        if (typeof metric === "undefined") {
            return;
        }

        emitChangeUpdate({
            metric,
            direction,
        });
    };

    const handlePeriodChange = (period: EcommerceChangesPeriod): void => {
        if (typeof metric === "undefined") {
            return;
        }

        emitChangeUpdate({
            metric,
            period,
        });
    };

    return (
        <SignalFiltersWrapper>
            <FlexRow
                alignItems="center"
                gap="8px"
            >
                <SignalTrafficDirectionsFilter
                    options={directionOptions}
                    selectedValue={change?.direction}
                    autoOpen={currentActiveKey === "direction"}
                    onValueChange={handleDirectionChange}
                />

                <I18n
                    data-automation="ecommerce-change-over-prefix"
                    element={StyledDropdownLabel}
                    translatable={{ key: "si.signals.ecommerce.filters.change-over-prefix" }}
                />
                <SignalPeriodsFilter<EcommerceChangesPeriod>
                    options={periodOptions}
                    selectedValue={change?.period}
                    autoOpen={currentActiveKey === "period"}
                    onValueChange={handlePeriodChange}
                />
            </FlexRow>
        </SignalFiltersWrapper>
    );
};

export default EcommerceChangeFiltersView;
